class GriptapeDrawer extends HTMLElement {
  constructor() {
    super();
    const initialVariant = this.getInitialVariant();

    if (initialVariant) {
      const deckDetails = this.getDeckDetails(initialVariant);

      if (deckDetails) {
        this.deckId = deckDetails.id;
        this.deckSKU = deckDetails.sku;
        this.deckName = deckDetails.name;
        // console.log("Initial deck details loaded:", deckDetails);
      } else {
        console.error("Failed to load initial deck details.");
      }
    } else {
      console.warn("No initial variant data found.");
    }

    this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const variant = payload?.data?.variant;
      if (!variant) {
        console.error("Variant data not found in payload.");
        return;
      }
      const deckDetails = this.getDeckDetails(variant);
      if (deckDetails) {
        this.deckId = deckDetails.id;
        this.deckSKU = deckDetails.sku;
        this.deckName = deckDetails.name;
        // console.log("Deck details updated on variant change:", deckDetails);
      } else {
        console.error("Failed to update deck details on variant change.");
      }
    });

    this.griptapeItems = this.querySelectorAll(".griptape-item");
    this.deckOptions = this.querySelector(".deck-options");
    if (this.deckOptions) {
      this.appliedSelector = this.deckOptions.querySelector('.deck-options__selector[data-id="applied"]');
      this.stanceSelector = this.deckOptions.querySelector('.deck-options__selector[data-id="stance"]');
    }
    this.addToCartButton = this.querySelector(".drawer__footer .button");
    this.deckIdInput = document.querySelector("input.product-variant-id");
    this.selectedGriptape = null;

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#GriptapeDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
  }

  connectedCallback() {
    this.setTriggerListeners();
    this.setGriptapeSelectionHandler();
    this.setDeckOptionsHandler();
    this.initializeAddToCartButton();

    // Reset the drawer to its initial state
    this.resetDrawerState();
  }

  getInitialVariant() {
    const scriptElement = document.querySelector("script[data-selected-variant]");

    if (!scriptElement) {
      console.error("Script element with data-selected-variant not found.");
      return null;
    }

    try {
      const variant = JSON.parse(scriptElement.textContent.trim());
      return variant;
    } catch (error) {
      console.error("Error parsing JSON from script element:", error);
      return null;
    }
  }

  setTriggerListeners() {
    if (!this.id) {
      console.error("Drawer must have an ID to associate triggers.");
      return;
    }

    // Select all triggers linked to this drawer
    const triggers = document.querySelectorAll(`[data-drawer-target="${this.id}"]`);

    if (!triggers || triggers.length === 0) {
      console.warn(`No triggers found for drawer with ID: ${this.id}`);
      return;
    }

    triggers.forEach((trigger) => {
      trigger.setAttribute("role", "button");
      trigger.setAttribute("aria-haspopup", "dialog");

      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(trigger); // Open the current drawer
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(this, containerToTrapFocusOn);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(this.activeElement);
    this.resetDrawerState();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  resetDrawerState() {
    // Deselect all griptape items
    this.griptapeItems.forEach((item) => item.classList.remove("selected"));
    this.selectedGriptape = null;

    // Reset the state of the parent .griptapes container
    const griptapesContainer = this.querySelector(".griptapes");
    if (griptapesContainer) {
      griptapesContainer.classList.remove("has-selection");
    }

    // Reset deck options and their selectors
    if (this.deckOptions) {
      this.deckOptions.classList.remove("active");

      if (this.appliedSelector) {
        this.resetSelector(this.appliedSelector);
      }

      if (this.stanceSelector) {
        this.resetSelector(this.stanceSelector);
      }
    }

    // Reset Add to Cart button to default state (Skip)
    this.setAddToCartButtonState({ isEnabled: true, label: "Skip" });
  }

  // Helper method to reset a specific selector
  resetSelector(selector) {
    selector.classList.remove("active");
    selector.classList.add("hidden"); // Add hidden back to the selector
    selector.querySelectorAll("input").forEach((input) => {
      input.checked = false; // Uncheck all inputs
    });
  }

  initializeAddToCartButton() {
    if (this.addToCartButton) {
      this.addToCartButton.disabled = false;
      this.addToCartButton.addEventListener("click", (event) => this.handleAddToCart(event));
    }
  }

  setGriptapeSelectionHandler() {
    if (!this.griptapeItems || this.griptapeItems.length === 0) {
      console.error("No griptape items found.");
      return;
    }

    this.griptapeItems.forEach((item) => {
      item.addEventListener("click", () => {
        // console.log("Selected griptape:", item);

        const stanceSelectorEnabled = item.dataset.stanceSelector === "true";

        this.toggleGriptapeSelection(item, stanceSelectorEnabled);
      });
    });
  }

  toggleGriptapeSelection(item, stanceSelectorEnabled) {
    const griptapeId = item.dataset.id; // Get griptape-item ID
    const griptapesContainer = this.querySelector(".griptapes"); // Assuming .griptapes is the parent container

    if (!griptapeId) {
      console.error("Griptape item does not have a valid data-id:", item);
      return;
    }

    if (item.classList.contains("selected")) {
      // Deselect the current item
      item.classList.remove("selected");
      this.selectedGriptape = null;
      this.resetDeckOptions(); // Reset deck options

      // Remove `has-selection` if no items are selected
      if (griptapesContainer) {
        const hasSelectedItem = Array.from(this.griptapeItems).some((el) => el.classList.contains("selected"));
        if (!hasSelectedItem) {
          griptapesContainer.classList.remove("has-selection");
        }
      }

      // console.log("Deselected griptape item:", item);
    } else {
      // Deselect all other items
      this.griptapeItems.forEach((el) => el.classList.remove("selected"));

      // Select the current item
      item.classList.add("selected");
      this.selectedGriptape = griptapeId;

      // Add `has-selection` to the parent container
      if (griptapesContainer) {
        griptapesContainer.classList.add("has-selection");
      }

      // Check conditions
      const isStanceEnabled = item.dataset.stanceSelector === "true";
      this.resetDeckOptions();
      this.showDeckOptions(isStanceEnabled);

      // console.log("Selected griptape item:", this.selectedGriptape);
    }

    // Update the Add to Cart button state
    this.updateAddToCartButtonState();
  }

  showDeckOptions(stanceSelectorEnabled) {
    if (this.deckOptions) {
      this.deckOptions.classList.remove("hidden");

      // Show appliedSelector
      if (this.appliedSelector) {
        this.appliedSelector.classList.remove("hidden");
        this.appliedSelector.classList.add("active");
      }

      // Handle stanceSelector
      if (this.stanceSelector) {
        const stanceInputs = this.stanceSelector.querySelectorAll("input");

        // Reset stanceSelector status
        this.stanceSelector.classList.add("hidden");
        this.stanceSelector.classList.remove("active");
        stanceInputs.forEach((input) => {
          input.checked = false;
        });

        // Only show stanceSelector if stanceSelectorEnabled is true and Applied = Yes
        const appliedInput = this.appliedSelector.querySelector("input[name='properties[Applied]']:checked");
        const appliedValue = appliedInput ? appliedInput.value : "No";

        if (stanceSelectorEnabled && appliedValue === "Yes") {
          this.stanceSelector.dataset.enabled = "true";
          this.stanceSelector.classList.remove("hidden");
          this.stanceSelector.classList.add("active");
        } else {
          this.stanceSelector.dataset.enabled = "false";
        }
      }
    }
  }

  resetDeckOptions() {
    if (this.deckOptions) {
      // Hide deck-options
      this.deckOptions.classList.add("hidden");

      // Reset appliedSelector
      if (this.appliedSelector) {
        this.appliedSelector.classList.add("hidden");
        this.appliedSelector.classList.remove("active");
        const appliedInputs = this.appliedSelector.querySelectorAll("input");
        appliedInputs.forEach((input) => {
          input.checked = false;
        });
      }

      // Reset stanceSelector
      if (this.stanceSelector) {
        this.stanceSelector.classList.add("hidden");
        this.stanceSelector.classList.remove("active");
        const stanceInputs = this.stanceSelector.querySelectorAll("input");
        stanceInputs.forEach((input) => {
          input.checked = false;
        });
      }
    }
  }

  setDeckOptionsHandler() {
    if (!this.appliedSelector || !this.stanceSelector) return;

    const appliedYes = this.appliedSelector.querySelector('input[value="Yes"]');
    const appliedNo = this.appliedSelector.querySelector('input[value="No"]');
    const stanceInputs = this.stanceSelector.querySelectorAll("input");

    const activateStance = () => {
      const selectedGriptape = this.querySelector(".griptape-item.selected");
      const appliedInput = this.appliedSelector.querySelector("input[name='properties[Applied]']:checked");
      const appliedValue = appliedInput ? appliedInput.value : "No";

      // Only show stanceSelector if matched conditions
      if (selectedGriptape && selectedGriptape.dataset.stanceSelector === "true" && appliedValue === "Yes") {
        this.stanceSelector.classList.remove("hidden");
        this.stanceSelector.classList.add("active");
      }

      // Update Add to Cart button state
      this.updateAddToCartButtonState();
    };

    const resetStance = () => {
      this.stanceSelector.classList.add("hidden");
      this.stanceSelector.classList.remove("active");
      stanceInputs.forEach((input) => {
        input.checked = false; // Reset input's status
      });

      // Update Add to Cart button state
      this.updateAddToCartButtonState();
    };

    appliedYes?.addEventListener("change", () => {
      if (appliedYes.checked) {
        activateStance();
      }
    });

    appliedNo?.addEventListener("change", () => {
      if (appliedNo.checked) {
        resetStance();
      }
    });

    stanceInputs.forEach((input) => {
      input.addEventListener("change", () => {
        // Update Add to Cart button state when stance is selected
        this.updateAddToCartButtonState();
      });
    });
  }

  getDeckDetails(variant) {
    if (!variant?.id) {
      console.error("Variant data is missing or invalid.");
      return null;
    }

    try {
      const sku = variant.sku?.trim() || variant.id.toString();
      const productName = variant.name?.split(" - ")[0]?.trim() || "Unknown Product";

      const deckDetails = {
        id: variant.id,
        sku,
        name: productName,
      };
      // console.log("Deck Details:", deckDetails);
      return deckDetails;
    } catch (error) {
      console.error("Error processing variant data:", error);
      return null;
    }
  }

  handleAddToCart(event) {
    event.preventDefault();

    if (!this.deckId || !this.deckSKU || !this.deckName) {
      console.error("Deck details not found. Cannot add to cart.");
      alert("Unable to add the deck to the cart. Please try again.");
      return;
    }

    // Set button to loading state
    this.setAddToCartButtonState({ isLoading: true });

    const items = [
      {
        id: this.deckId,
        quantity: 1,
      },
    ];

    if (this.selectedGriptape) {
      if (!this.appliedSelector) {
        console.error("Applied Selector not found.");
        this.setAddToCartButtonState({ isLoading: false });
        return;
      }

      const appliedInput = this.appliedSelector.querySelector("input[name='properties[Applied]']:checked");
      const appliedValue = appliedInput ? appliedInput.value : "";

      let stanceValue = "";
      if (appliedValue === "Yes" && this.stanceSelector) {
        const stanceInput = this.stanceSelector.querySelector("input[name='properties[Stance]']:checked");
        stanceValue = stanceInput ? stanceInput.value : "";
      }

      const griptapeProperties = {
        Applied: appliedValue,
        Stance: stanceValue,
        "Linked to": this.deckName,
        _linkedSKU: this.deckSKU,
      };

      const griptapeItem = {
        id: this.selectedGriptape,
        quantity: 1,
        properties: griptapeProperties,
      };

      items.push(griptapeItem);
    }

    const formData = { items };
    const config = fetchConfig("json");
    config.body = JSON.stringify(formData);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => {
        if (!response.ok) {
          return response.text().then((errorText) => {
            try {
              const errorJson = JSON.parse(errorText);
              throw errorJson;
            } catch {
              throw new Error(errorText || "Unexpected error");
            }
          });
        }
        return response.json();
      })
      .then(() => {
        this.close();
        this.triggerCartDrawerUpdate();
      })
      .catch((errorResponse) => {
        console.error(errorResponse);
        alert("Could not add items to the cart. Please try again.");
      })
      .finally(() => {
        // Remove loading state
        this.setAddToCartButtonState({ isLoading: false });
      });
  }

  setAddToCartButtonState({ isLoading = false, isEnabled = true, label = "Add to cart" }) {
    if (!this.addToCartButton) return;

    // Manage loading state
    if (isLoading) {
      this.addToCartButton.classList.add("loading");
      this.addToCartButton.querySelector(".loading__spinner").classList.remove("hidden");
      this.addToCartButton.disabled = true;
    } else {
      this.addToCartButton.classList.remove("loading");
      this.addToCartButton.querySelector(".loading__spinner").classList.add("hidden");
      this.addToCartButton.disabled = !isEnabled;
    }

    // Update text label inside the button
    const labelElement = this.addToCartButton.querySelector("span");
    if (labelElement) {
      labelElement.textContent = label;
    }
  }

  updateAddToCartButtonState() {
    if (!this.addToCartButton) return;

    if (!this.selectedGriptape) {
      // Default state when no griptape-item is selected
      this.setAddToCartButtonState({ isEnabled: true, label: "Skip" });
      return;
    }

    // Check applied input
    const appliedInput = this.appliedSelector.querySelector("input[name='properties[Applied]']:checked");
    const appliedValue = appliedInput ? appliedInput.value : null;

    if (!appliedValue) {
      // Disable button if Applied is not selected
      this.setAddToCartButtonState({ isEnabled: false, label: "Add to cart" });
      return;
    }

    const selectedGriptape = this.querySelector(".griptape-item.selected");
    const stanceSelectorEnabled = selectedGriptape?.dataset.stanceSelector === "true";

    if (!stanceSelectorEnabled) {
      // Allow Add to cart when Stance is disabled
      this.setAddToCartButtonState({ isEnabled: true, label: "Add to cart" });
      return;
    }

    if (appliedValue === "No") {
      // Enable button if Applied is No
      this.setAddToCartButtonState({ isEnabled: true, label: "Add to cart" });
      return;
    }

    if (appliedValue === "Yes") {
      // Check stance input
      const stanceInput = this.stanceSelector.querySelector("input[name='properties[Stance]']:checked");
      const isStanceSelected = Boolean(stanceInput);

      // Enable only if stance is selected
      this.setAddToCartButtonState({ isEnabled: isStanceSelected, label: "Add to cart" });
    }
  }

  triggerCartDrawerUpdate() {
    const cartDrawer = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    if (!cartDrawer) {
      console.error("Can't not find cart-drawer in DOM");
      return;
    }

    fetch(`${routes.root_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((parsedState) => {
        const transformedState = { sections: parsedState };
        // console.log(transformedState);

        if (typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(transformedState);
          cartDrawer.classList.remove("is-empty");
        } else {
          console.error("Method renderContents does not exist in cart-drawer");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define("griptape-drawer", GriptapeDrawer);
